import React, { useState, useEffect } from "react";
import {
  List,
  Empty,
  Tag,
  Divider,
  Cascader,
  Segmented,
  Button,
  Spin,
  Collapse,
  Tooltip,
} from "antd";
import {
  ThunderboltOutlined,
  GlobalOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  CompressOutlined,
  ExpandOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import { LibraryItemRenderer } from "./scope-and-adjustments-library";
import {
  DiscountType,
  orgUnitCascaderValue,
  orgUnitFromCascadeValue,
  PracticeArea,
  Matter,
  RelevantMatter,
  UpliftType,
} from "@design-system";
import { useQuery } from "@apollo/client";
import { GET_PHASES } from "~/graphql/query";
import { GetPhasesQuery, GetPhasesQueryVariables } from "~/gql/graphql";
import { SubTabType, TabItemMap } from "./types/library-item-types";
import { VPDLabel } from "~/context/taxonomy-context";

import styles from "./scope-and-adjustments-library.module.css";

const {
  "scope-library-item": scopeLibraryItemClassName,
  "scope-library-section": scopeLibrarySectionClassName,
  "suggested-item-icon": suggestedItemIconClassName,
  "practice-area-cascader": practiceAreaCascaderClassName,
  "practice-area-filter-section": practiceAreaFilterSectionClassName,
  "practice-area-scope-selector": practiceAreaScopeSelectorClassName,
  "selected-icon": selectedIconClassName,
  "selected-item": selectedItemClassName,
  "scope-library-matter-item": scopeLibraryMatterItemClassName,
  "scope-library-matter-title": scopeLibraryMatterTitleClassName,
  "scope-library-matter-client": scopeLibraryMatterClientClassName,
  "scope-library-matter-content": scopeLibraryMatterContentClassName,
  "matter-phases-container": matterPhasesContainerClassName,
  "task-item": taskItemClassName,
  "task-list": taskListClassName,
  "task-title": taskTitleClassName,
  "selected-matter-container": selectedMatterContainerClassName,
  "selected-matter-label": selectedMatterLabelClassName,
  "selected-matter-item": selectedMatterItemClassName,
  "matter-organisational-unit": matterOrganisationalUnitClassName,
  "empty-description-container": emptyDescriptionContainerClassName,
  "library-pagination": libraryPaginationClassName,
  "template-tag": templateTagClassName,
  "phase-display-button-row": phaseDisplayButtonRowClassName,
  "phase-display-header": phaseDisplayHeaderClassName,
  "phase-header-content": phaseHeaderContentClassName,
  "phase-copy-button": phaseCopyButtonClassName,
  "collapse-button-margin": collapseButtonMarginClassName,
  "phase-collapse": phaseCollapseClassName,
  "title-row-container": titleRowContainerClassName,
  "pinned-row": pinnedRowClassName,
} = styles as any;

/**
 * A type-safe function to get display text from different types of library items
 * This handles all the possible types in the LibraryItem union
 */
export function getLibraryItemText<T extends SubTabType>(
  subTab: T,
  item: TabItemMap[T] | string,
  displayAmounts: boolean = false,
  currencySymbol: string = ""
): string {
  if (!item) return "";

  if (typeof item === "string") {
    return item;
  }

  switch (subTab) {
    case "assumptions": {
      const assumption = item as TabItemMap["assumptions"];
      return assumption.text || "";
    }
    case "exclusions": {
      const exclusion = item as TabItemMap["exclusions"];
      return exclusion.text || "";
    }
    case "discounts": {
      const discount = item as TabItemMap["discounts"];
      return (
        discount.reason +
          (displayAmounts
            ? ` (${
                discount.type === DiscountType.Amount
                  ? `${currencySymbol}${discount.amount}`
                  : `${discount.amount}%`
              })`
            : "") || `Discount ${discount.id}`
      );
    }
    case "uplifts": {
      const uplift = item as TabItemMap["uplifts"];
      // Handle both library uplifts and matter uplifts
      if ("reason" in uplift && typeof uplift.reason === "string") {
        return uplift.reason;
      } else if ("baseUplift" in uplift && uplift.baseUplift) {
        return (
          uplift.baseUplift.reason +
          (displayAmounts
            ? ` (${
                uplift.type === UpliftType.Amount
                  ? `${currencySymbol}${uplift.amount}`
                  : `${uplift.amount}%`
              })`
            : "")
        );
      }
      return `Uplift ${uplift.id || 0}`;
    }
    case "disbursements": {
      const disbursement = item as TabItemMap["disbursements"];
      return (
        disbursement.name + (displayAmounts ? ` (${currencySymbol}${disbursement.amount})` : "") ||
        `Disbursement ${disbursement.id || 0}`
      );
    }
    case "scope": {
      return "";
    }
    default:
      const anyItem = item as any;
      if (anyItem.text) return anyItem.text;
      if (anyItem.name) return anyItem.name;
      if (anyItem.reason) return anyItem.reason;
      if (anyItem.id) return `Item ${anyItem.id}`;
      return "";
  }
}

/**
 * Generic function to check if two library items are equivalent
 * This is used to determine if an item is selected
 */
export function areLibraryItemsEqual<T extends SubTabType>(
  subTab: T,
  item1: TabItemMap[T],
  item2: TabItemMap[T]
): boolean {
  if (!item1 || !item2) return false;
  return getLibraryItemText(subTab, item1) === getLibraryItemText(subTab, item2);
}

/**
 * Helper function to filter items using search text
 * Ensures consistent filter behavior across all renderers
 */
export function filterItemsBySearchText<T extends SubTabType>(
  items: TabItemMap[T][],
  searchText: string,
  subTab: T
): TabItemMap[T][] {
  if (!searchText) return items;

  return items.filter((item) =>
    getLibraryItemText(subTab, item).toLowerCase().includes(searchText.toLowerCase())
  );
}

/**
 * Simple list renderer for string items
 */
export const SimpleStringListRenderer: React.FC<LibraryItemRenderer<string>> = ({
  items,
  selectedItems = [],
  loading = false,
  onSelectItem,
  searchText,
}) => {
  const filteredItems = Array.isArray(items)
    ? items.filter((item) =>
        searchText ? item.toLowerCase().includes(searchText.toLowerCase()) : true
      )
    : [];

  return (
    <List
      loading={loading}
      dataSource={filteredItems}
      renderItem={(item: string) => {
        const isSelected = selectedItems.includes(item);
        return (
          <List.Item
            className={`${scopeLibraryItemClassName} ${isSelected ? selectedItemClassName : ""}`}
            onClick={() => !isSelected && onSelectItem(item)}
          >
            <div style={{ flex: 1 }}>{item}</div>
            {isSelected && (
              <CheckCircleOutlined className={selectedIconClassName} style={{ fontSize: "18px" }} />
            )}
          </List.Item>
        );
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No items found</p>
                {searchText && <small>Try adjusting your search terms</small>}
              </div>
            }
          />
        ),
      }}
    />
  );
};

/**
 * Generic library item list renderer that can handle any LibraryItem type
 */
export interface LibraryListRendererProps<T extends SubTabType> {
  items: TabItemMap[T][];
  selectedItems?: TabItemMap[T][];
  loading?: boolean;
  onSelectItem: (item: TabItemMap[T]) => void;
  searchText: string;
  subTab: T;
  currencySymbol: string;
  historical: boolean;
}

export const LibraryListRenderer = <T extends SubTabType>({
  items,
  selectedItems = [],
  loading = false,
  onSelectItem,
  searchText,
  subTab,
  currencySymbol,
  historical,
}: LibraryListRendererProps<T>) => {
  // Use the common filter helper
  const filteredItems = historical
    ? items
    : Array.isArray(items)
    ? filterItemsBySearchText(items, searchText, subTab)
    : [];

  return (
    <List
      loading={loading}
      dataSource={filteredItems}
      renderItem={(item) => {
        const isSelected = selectedItems.some((selected) =>
          areLibraryItemsEqual(subTab, selected, item)
        );
        return (
          <List.Item
            className={`${scopeLibraryItemClassName} ${isSelected ? selectedItemClassName : ""}`}
            onClick={() => !isSelected && onSelectItem(item)}
          >
            <div style={{ flex: 1 }}>
              <div>{getLibraryItemText(subTab, item, historical, currencySymbol)}</div>
            </div>
            {isSelected && (
              <CheckCircleOutlined className={selectedIconClassName} style={{ fontSize: "18px" }} />
            )}
          </List.Item>
        );
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No items found</p>
                {searchText && <small>Try adjusting your search terms</small>}
              </div>
            }
          />
        ),
      }}
    />
  );
};

/**
 * A type-safe grouped list renderer that works with any LibraryItem type
 */
export interface GroupedListRendererProps<T extends SubTabType> {
  items: Record<string, TabItemMap[T][]>;
  selectedItems?: TabItemMap[T][];
  loading?: boolean;
  onSelectItem: (item: TabItemMap[T]) => void;
  searchText: string;
  subTab: T;
}

export const GroupedListRenderer = <T extends SubTabType>({
  items,
  selectedItems = [],
  loading = false,
  onSelectItem,
  searchText,
  subTab,
}: GroupedListRendererProps<T>) => {
  if (!items || Array.isArray(items)) {
    return null;
  }

  // Filter items in each category based on search text
  const filteredItems: Record<string, TabItemMap[T][]> = {};

  Object.entries(items).forEach(([category, categoryItems]) => {
    // Use the common filter helper
    const filtered = filterItemsBySearchText(categoryItems, searchText, subTab);

    if (filtered.length > 0) {
      filteredItems[category] = filtered;
    }
  });

  if (Object.keys(filteredItems).length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <p>No items found</p>
            {searchText && <small>Try adjusting your search terms</small>}
          </div>
        }
      />
    );
  }

  return (
    <div>
      <Collapse defaultActiveKey={Object.keys(filteredItems)} ghost>
        {Object.entries(filteredItems).map(([category, categoryItems]) => (
          <Collapse.Panel header={category} key={category}>
            <List
              loading={loading}
              dataSource={categoryItems}
              renderItem={(item) => {
                const isSelected = selectedItems.some((selected) =>
                  areLibraryItemsEqual(subTab, selected, item)
                );
                return (
                  <List.Item
                    className={`${scopeLibraryItemClassName} ${
                      isSelected ? selectedItemClassName : ""
                    }`}
                    onClick={() => !isSelected && onSelectItem(item)}
                  >
                    <div style={{ flex: 1 }}>
                      <div>{getLibraryItemText(subTab, item)}</div>
                    </div>
                    {isSelected && (
                      <CheckCircleOutlined
                        className={selectedIconClassName}
                        style={{ fontSize: "18px" }}
                      />
                    )}
                  </List.Item>
                );
              }}
            />
          </Collapse.Panel>
        ))}
      </Collapse>
    </div>
  );
};

/**
 * Component to display phases and tasks inline within the library
 */
interface PhaseDisplayProps {
  matterId: number;
  onCopyPhases?: () => void;
  onCopyPhase?: (phaseId: number) => void;
}

export const PhaseDisplay: React.FC<PhaseDisplayProps> = ({
  matterId,
  onCopyPhases,
  onCopyPhase,
}) => {
  const { loading, data } = useQuery<GetPhasesQuery, GetPhasesQueryVariables>(GET_PHASES, {
    variables: { matterId },
    skip: !matterId,
  });

  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const phases = data?.getPhases || [];

  // Set initial state when phases are loaded
  React.useEffect(() => {
    if (phases.length > 0) {
      // Default to all collapsed
      setExpandedPanels([]);
    }
  }, [phases]);

  const expandAllPanels = () => {
    setExpandedPanels(phases.map((phase) => phase.id.toString()));
  };

  const collapseAllPanels = () => {
    setExpandedPanels([]);
  };

  const handleCopyPhase = (phaseId: number, e: React.MouseEvent) => {
    // Stop propagation to prevent panel from toggling
    e.stopPropagation();
    if (phaseId !== undefined) {
      onCopyPhase && onCopyPhase(phaseId);
    }
  };

  return (
    <div className={matterPhasesContainerClassName}>
      {loading ? (
        <Spin tip="Loading phases..." />
      ) : phases.length === 0 ? (
        <Empty description="No phases found for this matter" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <div className={phaseDisplayButtonRowClassName}>
            <Button type="primary" onClick={onCopyPhases} disabled={phases.length === 0}>
              Copy All Phases
            </Button>
            <div>
              <Tooltip title="Collapse All">
                <Button
                  icon={<CompressOutlined />}
                  size="small"
                  onClick={collapseAllPanels}
                  className={collapseButtonMarginClassName}
                />
              </Tooltip>
              <Tooltip title="Expand All">
                <Button icon={<ExpandOutlined />} size="small" onClick={expandAllPanels} />
              </Tooltip>
            </div>
          </div>
          <Collapse
            className={phaseCollapseClassName}
            activeKey={expandedPanels}
            onChange={(keys) => setExpandedPanels(Array.isArray(keys) ? keys : [keys])}
          >
            {phases.map((phase) => (
              <Collapse.Panel
                header={
                  <div className={phaseHeaderContentClassName}>
                    <span>{phase.title}</span>
                    <Button
                      size="small"
                      type="primary"
                      onClick={(e) => handleCopyPhase(phase.id, e)}
                      className={phaseCopyButtonClassName}
                    >
                      Copy Phase
                    </Button>
                  </div>
                }
                key={phase.id}
              >
                <ul className={taskListClassName}>
                  {phase.tasks?.map((task) => (
                    <li key={task.id} className={taskItemClassName}>
                      <div className={taskTitleClassName}>{task.title}</div>
                    </li>
                  ))}
                </ul>
              </Collapse.Panel>
            ))}
          </Collapse>
        </>
      )}
    </div>
  );
};

/**
 * Component to render library content for the scope tab
 */
export interface ScopeLibraryProps {
  matterData?: RelevantMatter[];
  mattersLoading?: boolean;
  selectedMatterId?: number | null;
  selectedMatter?: Matter | null;
  onSelectMatter?: (matterId: number | null) => void;
  onCopyPhases: (matterId: number, phaseId?: number) => void;
  activeTab?: "library" | "historical" | "favourites";
  pinnedMatters?: RelevantMatter[];

  // Pagination props
  pagination?: {
    enabled?: boolean;
    currentPage?: number;
    pageSize?: number;
    totalCount?: number;
    onPageChange?: (page: number, pageSize: number) => void;
  };
}

export const ScopeLibrary: React.FC<ScopeLibraryProps> = ({
  matterData,
  mattersLoading,
  selectedMatterId,
  selectedMatter,
  onSelectMatter,
  onCopyPhases,
  activeTab = "library",
  pinnedMatters = [],
  // Pagination props
  pagination,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea", { textCase: "lower" });

  const [viewingPhases, setViewingPhases] = useState(!!selectedMatter);

  // Update local state when activeTab or selected matters change
  useEffect(() => {
    setViewingPhases(!!selectedMatter);
  }, [selectedMatter]);

  const handleMatterClick = (matterId: number) => {
    onSelectMatter?.(matterId);
    setViewingPhases(true);
  };

  const handleCopyPhases = () => {
    if (selectedMatterId) {
      onCopyPhases(selectedMatterId);
    }
  };

  const handleCopyPhase = (phaseId: number) => {
    if (selectedMatterId) {
      onCopyPhases(selectedMatterId, phaseId);
    }
  };

  const handlePaginationChange = (page: number, size: number) => {
    if (pagination?.onPageChange) {
      pagination.onPageChange(page, size);
    }
  };

  const isPinned = (matterId: number) => {
    return pinnedMatters.some((pinnedMatter) => pinnedMatter.id === matterId);
  };

  if (viewingPhases && selectedMatterId) {
    return (
      <PhaseDisplay
        matterId={selectedMatterId}
        onCopyPhases={handleCopyPhases}
        onCopyPhase={handleCopyPhase}
      />
    );
  }

  const showPagination = pagination?.enabled && activeTab === "favourites";

  // Filter out pinned matters from the regular list to avoid duplicates
  const filteredMatterData = matterData?.filter(
    (matter) => !pinnedMatters.some((pinnedMatter) => pinnedMatter.id === matter.id)
  );

  const shouldShowPinnedMatters = pinnedMatters.length > 0 && activeTab === "historical";

  return (
    <div>
      <div className={scopeLibrarySectionClassName}>
        {selectedMatter && (
          <div className={selectedMatterContainerClassName}>
            <div className={selectedMatterLabelClassName}>Selected Matter:</div>
            <MatterListItem
              matter={selectedMatter}
              className={selectedMatterItemClassName}
              isPinned={isPinned(selectedMatter.id)}
            />
          </div>
        )}

        {/* Pinned Matters Section */}
        {shouldShowPinnedMatters && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>
                Pinned Matters
                <PushpinOutlined style={{ marginLeft: "8px" }} />
              </h4>
              <List
                dataSource={pinnedMatters}
                renderItem={(item: RelevantMatter) => {
                  // Don't show the already selected matter again in the list
                  if (selectedMatter?.id === item.id) {
                    return null;
                  }

                  return (
                    <MatterListItem
                      matter={item}
                      onClick={() => handleMatterClick(item.id)}
                      isPinned={true}
                    />
                  );
                }}
                locale={{
                  emptyText: <></>,
                }}
              />
            </div>
            {filteredMatterData && filteredMatterData.length > 0 && (
              <Divider style={{ margin: "8px 0 16px 0" }} />
            )}
          </>
        )}

        {/* Regular Matters Section */}
        {shouldShowPinnedMatters && filteredMatterData && filteredMatterData.length > 0 && (
          <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>Other Matters</h4>
        )}
        <List
          loading={mattersLoading}
          dataSource={shouldShowPinnedMatters ? filteredMatterData : matterData}
          pagination={
            showPagination
              ? {
                  current: pagination?.currentPage,
                  pageSize: pagination?.pageSize,
                  total: pagination?.totalCount,
                  onChange: handlePaginationChange,
                  showSizeChanger: false,
                  position: "bottom",
                  size: "small",
                  className: libraryPaginationClassName,
                }
              : false
          }
          renderItem={(item: RelevantMatter) => {
            // Don't show the already selected matter again in the list
            if (selectedMatter?.id === item.id) {
              return null;
            }

            // Check if this matter is pinned
            const itemIsPinned = isPinned(item.id);

            return (
              <MatterListItem
                matter={item}
                onClick={() => handleMatterClick(item.id)}
                isPinned={itemIsPinned}
              />
            );
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className={emptyDescriptionContainerClassName}>
                    <p>No {activeTab} matters found</p>
                    {activeTab === "favourites" && (
                      <small>
                        You can mark matters as favourites from the matter details page.
                      </small>
                    )}
                    {activeTab === "historical" && (
                      <small>Try adjusting your search terms or {PA_LABEL} filter</small>
                    )}
                  </div>
                }
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

/**
 * Type-safe version of SuggestedLibraryListRenderer that works with LibraryItem types
 */
export interface SuggestedLibraryListRendererProps<T extends SubTabType> {
  items: TabItemMap[T][];
  selectedItems?: TabItemMap[T][];
  loading?: boolean;
  onSelectItem: (item: TabItemMap[T]) => void;
  searchText: string;
  subTab: T;
  suggestions?: {
    chosen: string[];
    suggested: string[];
  };
  suggestionsLoading?: boolean;
  matterOrgUnitId?: number | null;
  selectedOrgUnitId?: number | null;
  setSelectedOrgUnitId?: (orgUnitId: number | null) => void;
  practiceAreas: PracticeArea[];
  showLibraryItems?: boolean;
}

/**
 * Type-safe renderer for suggested library items
 */
export const SuggestedLibraryListRenderer = <T extends SubTabType>({
  items,
  selectedItems = [],
  suggestions,
  suggestionsLoading = false,
  loading = false,
  onSelectItem,
  searchText,
  matterOrgUnitId,
  selectedOrgUnitId,
  setSelectedOrgUnitId,
  practiceAreas,
  subTab,
  showLibraryItems = true,
}: SuggestedLibraryListRendererProps<T>) => {
  const PA_LABEL = VPDLabel("PracticeArea", { textCase: "lower" });
  const PA_LABEL_TITLE = VPDLabel("PracticeArea", { textCase: "title" });

  // Only work with array items
  if (!Array.isArray(items)) {
    return null;
  }

  // Determine if we're in "Firm Wide" mode
  const isFirmWideMode = selectedOrgUnitId === null;

  const paOptions = practiceAreas
    .filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  // Initialize arrays for suggested and regular items
  let suggestedItems: TabItemMap[T][] = [];
  let regularItems: TabItemMap[T][] = [];

  // Filter all items based on search text first
  const searchFilteredItems = items.filter((item) =>
    searchText
      ? getLibraryItemText(subTab, item).toLowerCase().includes(searchText.toLowerCase())
      : true
  );

  if (suggestions) {
    const { chosen, suggested } = suggestions;
    const suggestionTexts = new Set<string>([
      ...(chosen || []),
      ...(suggested || []),
    ]);

    // Find existing items that match the "chosen" strings
    const chosenItems = searchFilteredItems.filter((item) =>
      chosen.includes(getLibraryItemText(subTab, item))
    );

    // Create new items from "suggested" strings
    // We need to create objects that match the appropriate TabItemMap type
    const suggestedItemsFromStrings = suggested
      .filter((text) => (searchText ? text.toLowerCase().includes(searchText.toLowerCase()) : true))
      .map((text, index) => {
        // Create appropriate type of item based on subTab
        switch (subTab) {
          case "assumptions":
            return { id: -(index + 1), text } as TabItemMap[T];
          case "exclusions":
            return { id: -(index + 1), text } as TabItemMap[T];
          case "discounts":
            return { id: -(index + 1), reason: text } as TabItemMap[T];
          case "uplifts":
            return {
              id: -(index + 1),
              baseUplift: { id: -(index + 1), reason: text },
            } as TabItemMap[T];
          case "disbursements":
            return { id: -(index + 1), name: text } as TabItemMap[T];
          default:
            return { id: -(index + 1), text } as TabItemMap[T];
        }
      });

    // Combine chosen and suggested items for the "Suggested" section
    suggestedItems = [...chosenItems, ...suggestedItemsFromStrings];

    // All other items that aren't in the "chosen" list go to the regular section
    regularItems = searchFilteredItems.filter(
      (item) => !suggestionTexts.has(getLibraryItemText(subTab, item))
    );
  } else {
    // If no suggestions provided, all items go to regular items
    regularItems = searchFilteredItems;
  }

  return (
    <div>
      {/* Practice Area Filter Section */}
      <div className={practiceAreaFilterSectionClassName}>
        <div className={practiceAreaScopeSelectorClassName}>
          <Segmented
            options={[
              {
                label: "Firm-wide",
                value: "firm-wide",
                icon: <GlobalOutlined />,
              },
              {
                label: `${PA_LABEL_TITLE} specific`,
                value: "practice-area",
                icon: <ApartmentOutlined />,
              },
            ]}
            value={isFirmWideMode ? "firm-wide" : "practice-area"}
            onChange={(value) => {
              if (value === "firm-wide") {
                setSelectedOrgUnitId?.(null);
              } else if (isFirmWideMode) {
                if (paOptions.length > 0 && paOptions[0].value) {
                  setSelectedOrgUnitId?.(matterOrgUnitId ?? paOptions[0].value);
                }
              }
            }}
          />
        </div>

        {!isFirmWideMode && showLibraryItems && (
          <div className={practiceAreaCascaderClassName}>
            <Cascader
              style={{ width: "100%" }}
              allowClear={false}
              showSearch={true}
              placeholder={`Select a ${PA_LABEL}`}
              value={
                selectedOrgUnitId !== null
                  ? orgUnitCascaderValue(practiceAreas, selectedOrgUnitId)
                  : undefined
              }
              options={paOptions}
              onChange={(val: any) => {
                const orgUnitValue = orgUnitFromCascadeValue(val);
                setSelectedOrgUnitId?.(orgUnitValue);
              }}
            />
          </div>
        )}
      </div>

      {/* Display Empty state if no items after filtering */}
      {suggestedItems.length === 0 && regularItems.length === 0 && !suggestionsLoading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <p>No items found</p>
              {searchText && <small>Try adjusting your search terms</small>}
            </div>
          }
        />
      ) : (
        <>
          {/* Suggested Items Section */}
          {(suggestedItems.length > 0 || (suggestionsLoading && (!showLibraryItems || regularItems.length === 0))) && (
            <div className={scopeLibrarySectionClassName}>
              {showLibraryItems && (
                <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>Suggested items</h4>
              )}
              <List
                loading={suggestionsLoading && suggestedItems.length === 0}
                dataSource={suggestedItems}
                renderItem={(item) => {
                  const isSelected = selectedItems.some((selected) =>
                    areLibraryItemsEqual(subTab, selected, item)
                  );

                  // Check if this is a newly suggested item (with negative ID)
                  const isNewSuggestion = "id" in item && item.id !== undefined && item.id < 0;

                  return (
                    <List.Item
                      className={`${scopeLibraryItemClassName} ${
                        isSelected ? selectedItemClassName : ""
                      }`}
                      onClick={() => !isSelected && onSelectItem(item)}
                    >
                      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                        {isNewSuggestion && (
                          <ThunderboltOutlined
                            className={suggestedItemIconClassName}
                            style={{ marginRight: "8px" }}
                          />
                        )}
                        <div>{getLibraryItemText(subTab, item)}</div>
                      </div>
                      {isSelected && (
                        <CheckCircleOutlined
                          className={selectedIconClassName}
                          style={{ fontSize: "18px" }}
                        />
                      )}
                    </List.Item>
                  );
                }}
              />
            </div>
          )}

          {/* Divider only shown when both sections have items and regular items are shown */}
          {suggestedItems.length > 0 && regularItems.length > 0 && showLibraryItems && (
            <Divider style={{ margin: "16px 0" }} />
          )}

          {/* Regular Items Section */}
          {regularItems.length > 0 && showLibraryItems && (
            <div className={scopeLibrarySectionClassName}>
              {suggestedItems.length > 0 && (
                <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>Other items</h4>
              )}
              <List
                loading={loading}
                dataSource={regularItems}
                renderItem={(item) => {
                  const isSelected = selectedItems.some((selected) =>
                    areLibraryItemsEqual(subTab, selected, item)
                  );
                  return (
                    <List.Item
                      className={`${scopeLibraryItemClassName} ${
                        isSelected ? selectedItemClassName : ""
                      }`}
                      onClick={() => !isSelected && onSelectItem(item)}
                    >
                      <div style={{ flex: 1 }}>
                        <div>{getLibraryItemText(subTab, item)}</div>
                      </div>
                      {isSelected && (
                        <CheckCircleOutlined
                          className={selectedIconClassName}
                          style={{ fontSize: "18px" }}
                        />
                      )}
                    </List.Item>
                  );
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Reusable component for displaying matter items in lists with consistent styling
 */
interface MatterListItemProps {
  matter: Matter | RelevantMatter;
  onClick?: () => void;
  showTemplateTag?: boolean;
  className?: string;
  isPinned?: boolean;
}

export const MatterListItem: React.FC<MatterListItemProps> = ({
  matter,
  onClick,
  showTemplateTag = false,
  className,
  isPinned = false,
}) => {
  const TEMPLATE_LABEL_TITLE = VPDLabel("Template", { textCase: "title" });

  return (
    <List.Item
      className={`${scopeLibraryMatterItemClassName} ${className || ""} ${
        isPinned ? pinnedRowClassName : ""
      }`}
      onClick={onClick}
    >
      <div className={scopeLibraryMatterContentClassName}>
        <div className={titleRowContainerClassName}>
          <div className={scopeLibraryMatterTitleClassName}>
            <span>{matter.title}</span>
          </div>
          {showTemplateTag && (
            <Tag color="blue" className={templateTagClassName}>
              {TEMPLATE_LABEL_TITLE}
            </Tag>
          )}
        </div>
        <div className={scopeLibraryMatterClientClassName}>
          {matter.client?.name && (
            <>
              {matter.client.name}
              {matter.createdAt && ` • ${new Date(matter.createdAt).toLocaleDateString()}`}
            </>
          )}
          {matter.organisationalUnit && (
            <span className={matterOrganisationalUnitClassName}>
              {" "}
              {matter.client?.name ? "| " : ""}
              {matter.organisationalUnit.name}
            </span>
          )}
        </div>
      </div>
    </List.Item>
  );
};
